import {discordClientID, functions, globalSettings, isProd} from "./config";
import * as firebase from "firebase/app";

function getHashVariable(variable) {
  var query = window.location.hash.substring(1);
  var vars = query.split('&');
  for (var i = 0; i < vars.length; i++) {
    var pair = vars[i].split('=');
    if (decodeURIComponent(pair[0]) === variable) {
      return decodeURIComponent(pair[1]);
    }
  }
}

var exchangeDiscordToken = functions.httpsCallable('exchangeDiscordToken');
var discordAuth = functions.httpsCallable('discordAuth');
export var postToDiscord = functions.httpsCallable('postToDiscord');

export function ensureDiscordAuth() {
  if (globalSettings.disableAuthentication && !isProd) {
    console.log('🔓 Auth disabled for local development');
    return
  }
  if (window.location.hash.indexOf("access_token") !== -1) {
    console.log('🎯 Discord OAuth callback detected!');
    console.log('📝 Full hash:', window.location.hash);

    let token = getHashVariable("access_token");

    if (token) {
      // Log token info (mask most of it for security)
      const tokenPreview = token.substring(0, 10) + '...' + token.substring(token.length - 10);
      console.log('✅ Discord token received:', tokenPreview);
      console.log('📏 Token length:', token.length);

      localStorage.setItem("token", token);
      console.log('💾 Token saved to localStorage');

      window.location.hash = "";
      console.log('🧹 URL hash cleared');
    } else {
      console.error('❌ access_token found in hash but could not extract token!');
    }
  } else if (localStorage.getItem("token")) {
    console.log('✅ Existing token found in localStorage');
  } else {
    console.log('❌ No token found, forcing Discord auth...');
    forceDiscordAuth()
  }
}

export function forceDiscordAuth() {
  console.log('🚀 Initiating Discord OAuth flow...');
  console.log('🔑 Client ID:', discordClientID);
  console.log('🔄 Redirect URI:', window.location.origin);

  localStorage.removeItem("token");
  console.log('🗑️  Cleared existing token from localStorage');

  // Also sign out of Firebase
  firebase.auth().signOut().catch(() => {});
  console.log('🚪 Signed out of Firebase');

  const authUrl = `https://discord.com/api/oauth2/authorize?client_id=${discordClientID}&redirect_uri=${encodeURIComponent(window.location.origin)}&response_type=token&scope=identify%20guilds`;
  console.log('🌐 Redirecting to Discord:', authUrl);

  window.location = authUrl;
}

export function toggleAdmin() {
  if (localStorage.getItem("noAdmin")) {
    localStorage.removeItem("noAdmin")
  } else {
    localStorage.setItem("noAdmin", "1")
  }
  window.location.href = window.location.href;
}

/**
 * Signs the user into Firebase using a Discord access token.
 * Returns a promise that resolves when Firebase sign-in is complete.
 */
async function signInWithCustomAuth(discordToken) {
  try {
    // Log Discord token info (masked for security)
    const tokenPreview = discordToken.substring(0, 10) + '...' + discordToken.substring(discordToken.length - 10);
    console.log('🔄 Exchanging Discord token for Firebase token:', tokenPreview);
    console.log('📏 Token length being sent:', discordToken.length);
    console.log('📦 Calling exchangeDiscordToken with:', { token: '***' });

    // 1. Exchange Discord token for Firebase Custom Token
    const response = await exchangeDiscordToken({ token: discordToken });

    console.log('📬 Response received from exchangeDiscordToken:', response);
    const customToken = response.data.firebaseToken;

    console.log('✅ Received Firebase custom token from exchangeDiscordToken function');
    console.log('🔐 Signing into Firebase with custom token...');

    // 2. Sign user into Firebase Authentication
    await firebase.auth().signInWithCustomToken(customToken);

    console.log('✅ Firebase sign-in successful');

    // 3. Wait for auth state to be fully established and get ID token
    console.log('⏳ Waiting for Firebase auth state to be ready...');
    await new Promise((resolve) => {
      const unsubscribe = firebase.auth().onAuthStateChanged(async (user) => {
        if (user) {
          console.log('✅ Firebase auth state confirmed, user:', user.uid);
          // Force ID token refresh to ensure it's available for callable functions
          const idToken = await user.getIdToken(true);
          console.log('🎫 ID token refreshed and ready');
          console.log('🎫 Token preview:', idToken.substring(0, 20) + '...');
          unsubscribe();
          resolve();
        }
      });
    });

    // 4. Add a small delay to ensure Firebase Functions SDK picks up the auth context
    // This is necessary because there can be a race condition between auth state
    // propagation and callable function invocation
    console.log('⏳ Waiting for auth context to propagate to Functions SDK...');
    await new Promise(resolve => setTimeout(resolve, 500));

  } catch (error) {
    console.error('❌ Firebase sign-in failed:', error);
    console.error('❌ Error details:', error.code, error.message);
    throw error;
  }
}

/**
 * Calls the discordAuth function to validate Discord token and get user info.
 * No Firebase auth required - validates Discord token directly.
 */
function callSecuredDiscordAuth(discordToken) {
  if (!discordToken) {
    console.error('❌ No Discord token provided to callSecuredDiscordAuth');
    forceDiscordAuth();
    return Promise.reject(new Error('No Discord token'));
  }

  // Log token info (masked for security)
  const tokenPreview = discordToken.substring(0, 10) + '...' + discordToken.substring(discordToken.length - 10);
  console.log('📞 Calling discordAuth with Discord token:', tokenPreview);

  return discordAuth({
    token: discordToken,
    fetchRoles: !globalSettings.disableRoleFetch,
  }).then((response) => response.data).catch((error) => {
    console.error('❌ discordAuth error:', error);
    if (error.code && error.code.toLowerCase() === "unauthenticated") {
      console.error('🔒 Unauthenticated error (Discord token may be expired), forcing re-auth');
      forceDiscordAuth()
    } else {
      throw error;
    }
  });
}

export function getUserInfo() {
  // Local development bypass
  if (globalSettings.disableAuthentication && !isProd) {
    return Promise.resolve({
      id: globalSettings.hardcodedUserId,
      name: "Local User",
      member: true,
      admin: true,
      engineer: true,
      finance: true,
      superuser: true,
    })
  }

  let discordToken = localStorage.getItem("token");

  if (discordToken) {
    // Log token info (masked for security)
    const tokenPreview = discordToken.substring(0, 10) + '...' + discordToken.substring(discordToken.length - 10);
    console.log('🔑 Discord token found, authenticating...', tokenPreview);

    // Call discordAuth first (doesn't require Firebase auth)
    // IMPORTANT: Pass the token as a parameter instead of re-reading from localStorage
    return callSecuredDiscordAuth(discordToken)
      .then(async (userData) => {
        // Then sign into Firebase in the background for Firestore access
        // This happens async and doesn't block returning user data
        if (!firebase.auth().currentUser) {
          console.log('🔐 Signing into Firebase for Firestore access...');
          signInWithCustomAuth(discordToken).catch(error => {
            console.error('⚠️ Firebase sign-in failed (non-blocking):', error);
          });
        }
        return userData;
      })
      .catch(error => {
        console.error("❌ Discord auth failed:", error);
        // Force re-auth on failure
        forceDiscordAuth();
      });
  }

  // No token found, force Discord OAuth
  console.log('❌ No Discord token found, forcing auth...');
  forceDiscordAuth();
}
